<script setup lang="ts">
import { dialogEvents } from "@/data-views/dialogs";

type ProcessStatus = "pending" | "running" | "success" | "error";

interface ProcessStatusStep {
  key: string;
  label: string;
  status: ProcessStatus;
  detail?: string;
}

const isVisible = ref(false);
const state = reactive<{
  title: string;
  message: string;
  payload: string;
  status: "running" | "success" | "error";
  errorMessage: string;
  steps: ProcessStatusStep[];
}>({
  title: "",
  message: "",
  payload: "",
  status: "running",
  errorMessage: "",
  steps: [],
});

function iconForStatus(status: ProcessStatus): string {
  switch (status) {
    case "running":
      return "tabler-loader-2";
    case "success":
      return "tabler-circle-check";
    case "error":
      return "tabler-alert-circle";
    default:
      return "tabler-clock";
  }
}

function colorForStatus(status: ProcessStatus): string {
  switch (status) {
    case "running":
      return "info";
    case "success":
      return "success";
    case "error":
      return "error";
    default:
      return "default";
  }
}

function load(detail: {
  title: string;
  message?: string;
  payload?: string;
  steps?: ProcessStatusStep[];
}): void {
  state.title = detail.title;
  state.message = detail.message ?? "";
  state.payload = detail.payload ?? "";
  state.status = "running";
  state.errorMessage = "";
  state.steps = detail.steps ?? [];
}

function update(detail: {
  title?: string;
  message?: string;
  payload?: string;
  state?: "running" | "success" | "error";
  errorMessage?: string;
  steps?: ProcessStatusStep[];
}): void {
  if (detail.title !== undefined) state.title = detail.title;
  if (detail.message !== undefined) state.message = detail.message;
  if (detail.payload !== undefined) state.payload = detail.payload;
  if (detail.state !== undefined) state.status = detail.state;
  if (detail.errorMessage !== undefined) state.errorMessage = detail.errorMessage;
  if (detail.steps !== undefined) state.steps = detail.steps;
}

onMounted(() => {
  window.addEventListener(
    dialogEvents.ProcessStatusDialog.OPEN,
    ({ detail }: any) => {
      load(detail);
      isVisible.value = true;
    },
  );

  window.addEventListener(
    dialogEvents.ProcessStatusDialog.UPDATE,
    ({ detail }: any) => {
      update(detail);
    },
  );

  window.addEventListener(dialogEvents.ProcessStatusDialog.CLOSE, () => {
    isVisible.value = false;
  });
});
</script>

<template>
  <VDialog max-width="720" persistent :model-value="isVisible">
    <VCard class="px-6 py-4">
      <VCardTitle class="px-0 pb-2">
        <div class="d-flex align-center justify-space-between w-100">
          <span>{{ state.title }}</span>
          <VChip :color="colorForStatus(state.status)" size="small">
            {{ $t(state.status === "running" ? "Busy" : state.status === "success" ? "Completed" : "Failed") }}
          </VChip>
        </div>
      </VCardTitle>

      <VCardText class="px-0 pt-2 process-status-content">
        <p v-if="state.message" class="mb-4">
          {{ state.message }}
        </p>

        <div v-for="step in state.steps" :key="step.key" class="d-flex align-start mb-3">
          <VIcon
            :class="step.status === 'running' ? 'process-status-spin mt-1 mr-3' : 'mt-1 mr-3'"
            :color="colorForStatus(step.status)"
          >
            {{ iconForStatus(step.status) }}
          </VIcon>
          <div>
            <div class="font-weight-medium">{{ step.label }}</div>
            <div v-if="step.detail" class="text-medium-emphasis text-body-2 process-status-step-detail">
              {{ step.detail }}
            </div>
          </div>
        </div>

        <VAlert
          v-if="state.errorMessage"
          type="error"
          variant="tonal"
          class="mt-4 process-status-error"
        >
          {{ state.errorMessage }}
        </VAlert>

        <details v-if="state.payload" class="mt-4 process-status-payload-details">
          <summary class="text-subtitle-2">Transsmart payload</summary>
          <pre class="process-status-payload mt-2">{{ state.payload }}</pre>
        </details>
      </VCardText>

      <VCardActions class="px-0 pt-2 justify-end">
        <VBtn
          :disabled="state.status === 'running'"
          color="primary"
          @click="isVisible = false"
        >
          {{ $t("Close") }}
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style scoped>
.process-status-spin {
  animation: process-status-spin 1s linear infinite;
}

.process-status-error {
  white-space: pre-wrap;
  word-break: break-word;
}

.process-status-content {
  max-height: 70vh;
  overflow: auto;
}

.process-status-step-detail {
  white-space: pre-wrap;
}

.process-status-payload-details {
  cursor: pointer;
}

.process-status-payload {
  white-space: pre-wrap;
  word-break: break-word;
  background: rgb(var(--v-theme-surface-variant));
  border-radius: 8px;
  padding: 12px;
  font-size: 12px;
  line-height: 1.5;
}

@keyframes process-status-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
